import React from "react"
import { FormikProvider, useFormik, useFormikContext } from "formik"
import { observer, useLocalObservable } from "mobx-react-lite"
import * as yup from "yup"
import clsx from "clsx"
import { useAlert } from "react-alert"

import { useController, useStore } from "@store/index"
import SearchInput from "@pages/launch-solutions/SolutionTabsContainer/SearchInput"
import { useSolution } from "@pages/launch-solutions/ActiveSolutionContext"
import useActiveAvatar from "@pages/search/ActiveAvatarContext/useActiveAvatar"
import ShiftContainer from "@components/ui/ShiftContainer/ShiftContainer"
import { getReportDescriptionByType } from "@framework/constants/workbook"
import useClickOutside from "@components/hooks/useOutsideClick"
import { Option } from "@framework/types/utils"
import { WorkbookReportTypeName } from "@framework/types/workbook-report"
import {
  SEARCH_MODE_OPTIONS,
  SearchMode,
} from "@framework/types/search-mode"
import { ANSWER_FORMAT_OPTIONS } from "@framework/types/answer-format"
import Icon from "@components/ui/Icon/Icon"
import Button from "@components/ui/Button/Button"
import List from "@components/ui/List/List"
import Chip from "@components/ui/Chip/Chip"
import Checkbox from "@components/ui/Checkbox/Checkbox"
import FileCard from "@components/ui/FileCard"
import QueryAttachment from "@store/search/query-attachment"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/Tooltip/v2/Tooltip"

import WorkbookReportTemplate from "./WorkbookReportTemplate"
import {
  SearchWidgetContextState,
  SearchWidgetProvider,
  useSearchWidget,
} from "./SearchWidgetContext"

import styles from "./SearchWidget.module.sass"

const validationSchema = yup.object({
  query: yup.string().trim().min(1).default(""),
})

export interface Props {
  loading?: boolean
}

export const SearchWidget: React.FC<Props> = observer(({ loading }) => {
  const { solution } = useSolution()
  const alert = useAlert()

  const { avatar } = useActiveAvatar()

  const {
    solutionsStore: { appliedFilters },
    productComparisonStore: { productStore },
    factFinderSolutionStore: { searchFlowStore, queryAttachments },
    contentManagerStore: { companies },
  } = useStore()

  const { factFinderSolutionController: controller } = useController()

  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const searchWidgetContext = useLocalObservable(
    () => new SearchWidgetContextState()
  )

  const isSubmittingRef = React.useRef(false)

  const handleSubmit = async (data: any) => {
    if (isSubmittingRef.current) {
      return
    }

    if (queryAttachments.attachments.some((item) => item.isLoading)) {
      alert.error("Please wait for the attachment to finish uploading.")
      return
    }

    if (queryAttachments.hasAttachments && !queryAttachments.isLoaded) {
      alert.error(
        "Attachment is still processing. Please wait a moment and try again."
      )
      return
    }

    if (!solution?.id) {
      alert.error("Unable to start search. Please refresh and try again.")
      return
    }

    isSubmittingRef.current = true
    const submittedQuery = data.query

    // Clear immediately so the field does not stay filled while "Thinking..."
    formik.setFieldValue("query", "")

    try {
      const started = await controller.search(
        submittedQuery,
        avatar,
        solution.id,
        appliedFilters,
        companies
      )

      if (!started) {
        formik.setFieldValue("query", submittedQuery)
        if (searchFlowStore.isAnyLoading()) {
          alert.error("A search is already in progress. Please wait or stop it first.")
        } else {
          alert.error("Could not start search. Please try again.")
        }
      }
    } catch (error: any) {
      formik.setFieldValue("query", submittedQuery)
      if (error?.message === "ATTACHMENT_NOT_READY") {
        alert.error(
          "Attachment is still processing. Please wait a moment and try again."
        )
        return
      }

      alert.error("Could not start search. Please try again.")
    } finally {
      isSubmittingRef.current = false
    }
  }

  const formik = useFormik({
    initialValues: validationSchema.getDefault(),
    validationSchema,
    validateOnChange: true,
    onSubmit: handleSubmit,
  })

  const isSearching = searchFlowStore.isAnyLoading()

  React.useEffect(() => {
    productStore.loadProducts({
      filters: { search: "" },
      pagination: { ...productStore.productsMetaData, pageSize: 500 },
    })
  }, [])

  React.useEffect(() => {
    if (loading) searchWidgetContext.setActive(false)
  }, [loading])

  React.useLayoutEffect(() => {
    if (searchFlowStore.reportCandidate) {
      searchWidgetContext.setActive(true)
    }
  }, [searchFlowStore.reportCandidate])

  React.useEffect(() => {
    controller.loadAutocomplete("")
  }, [])

  useClickOutside({
    element: rootRef,
    onClickOutside: () => searchWidgetContext.setActive(false),
    onClickInside: () => searchWidgetContext.setActive(true),
  })

  return (
    <FormikProvider value={formik}>
      <SearchWidgetProvider value={searchWidgetContext}>
        <div
          ref={rootRef}
          className={clsx(styles.root, {
            [styles.focused]: searchWidgetContext.isActive,
          })}
        >
          <div className={styles.box}>
            <WorkflowHeader />

            <ShiftContainer>
              {searchFlowStore.reportCandidate ? (
                <WorkbookReportTemplate
                  loading={loading}
                  reportSuggestion={searchFlowStore.reportCandidate}
                  onSubmit={() => {
                    searchFlowStore.generateReport(null)
                  }}
                  onCancel={() => {
                    searchFlowStore.generateReport(null)
                  }}
                  key={searchFlowStore.reportCandidate.type}
                />
              ) : (
                <SearchForm loading={isSearching} key="search" />
              )}
            </ShiftContainer>
          </div>

          <SettingsFooter />
        </div>
      </SearchWidgetProvider>
    </FormikProvider>
  )
})

export const WorkflowHeader: React.FC<Props> = observer(({ loading }) => {
  const {
    factFinderSolutionStore: { searchFlowStore, searchAutocomplete },
  } = useStore()

  const options: Option[] = React.useMemo(
    () => [
      {
        name: "PRODUCTQUERY",
        value: "Product Query",
        icon: "search",
      },
      ...searchAutocomplete.data.map((it) => {
        const item = getReportDescriptionByType(it.type)
        return {
          name: it.type,
          value: item.displayName,
          icon: item.icon,
        }
      }),
    ],
    [searchAutocomplete.data]
  )

  React.useEffect(() => {
    const selectedWorkflow = searchFlowStore.reportCandidate
    if (
      selectedWorkflow != null &&
      !options.find((it) => it.name === selectedWorkflow.type)
    )
      searchFlowStore.generateReport(null)
  }, [options])

  return (
    <div className={styles.header}>
      <List direction="row" gutter="8" align="center">
        {options.map((it) => {
          const isActive =
            it.name ===
            (searchFlowStore.reportCandidate != null
              ? searchFlowStore.reportCandidate.type
              : options[0].name)

          return (
            <Button
              title={it.value}
              color="default"
              variant={isActive ? "contained" : "outlined"}
              size={isActive ? "medium" : "small"}
              before={<Icon name={it.icon} />}
              disabled={loading}
              onClick={() => {
                if (it.name === options[0].name)
                  searchFlowStore.generateReport(null)
                else
                  searchFlowStore.generateReport({
                    type: it.name as WorkbookReportTypeName,
                    data: {},
                  })
              }}
              key={it.name}
            >
              <span className={styles.nowrap}>{it.value}</span>
            </Button>
          )
        })}
      </List>
    </div>
  )
})

export const SearchForm: React.FC<Props> = observer(({ loading }) => {
  const formik = useFormikContext()
  const {
    factFinderSolutionStore: { queryAttachments },
  } = useStore()
  const { factFinderSolutionController: controller } = useController()

  const handleAttachmentRemove = (
    event: React.MouseEvent,
    attachment: QueryAttachment
  ) => {
    event.preventDefault()
    event.stopPropagation()
    controller.deleteAttachment(attachment)
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      {queryAttachments.attachments.length > 0 && (
        <div className={styles.attachmentsRow}>
          {queryAttachments.attachments.map((item) => (
            <FileCard
              key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
              name={item.file.name}
              size="small"
              loading={item.isLoading}
              onRemove={(event) => handleAttachmentRemove(event, item)}
            />
          ))}
        </div>
      )}

      <section className={styles.searchContainer}>
        <SearchInput loading={loading} />
      </section>
    </form>
  )
})

const SettingsFooter: React.FC = observer(() => {
  const {
    restrictionsStore: access,
    factFinderSolutionStore: { searchFlowStore },
  } = useStore()

  const searchWidgetContext = useSearchWidget()

  const searchModeOptions = React.useMemo(
    () =>
      SEARCH_MODE_OPTIONS.filter(
        (option) =>
          option.value !== SearchMode.ONLINE || access.allowOnlineSearch
      ),
    [access.allowOnlineSearch]
  )

  React.useEffect(() => {
    if (
      !access.allowOnlineSearch &&
      searchFlowStore.searchMode === SearchMode.ONLINE
    ) {
      searchFlowStore.setSearchMode(SearchMode.QUICK)
    }
  }, [access.allowOnlineSearch, searchFlowStore])

  React.useEffect(() => {
    if (searchFlowStore.reportCandidate != null)
      searchWidgetContext.showSettings(false)
  }, [searchFlowStore.reportCandidate])

  const active = searchWidgetContext.isSettingsShown
  const isSearching = searchFlowStore.isAnyLoading()
  const showWebResearch =
    access.allowWebSearch && searchFlowStore.searchMode !== SearchMode.ONLINE

  return (
    <div
      className={clsx(styles.settingsFooter, {
        [styles.visible]: active,
      })}
    >
      <div className={styles.body}>
        <div className={styles.modeRow}>
          <List
            direction="row"
            gutter="8"
            align="center"
            className={styles.modeGroup}
          >
            {searchModeOptions.map((option) => {
              const isActive = searchFlowStore.searchMode === option.value

              return (
                <Tooltip
                  key={option.value}
                  color="primary"
                  delay={600}
                  closeOnTargetPress
                >
                  <TooltipTrigger>
                    <Button
                      title={option.label}
                      color="default"
                      variant={isActive ? "contained" : "outlined"}
                      size={isActive ? "medium" : "small"}
                      disabled={isSearching}
                      onClick={() => searchFlowStore.setSearchMode(option.value)}
                    >
                      {option.label}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{option.description}</TooltipContent>
                </Tooltip>
              )
            })}
          </List>

          {showWebResearch && (
            <Tooltip color="primary" delay={600} closeOnTargetPress>
              <TooltipTrigger>
                <Chip
                  variant="rounded"
                  className={clsx(styles.toolChip, styles.webResearchChip, {
                    [styles.webResearchActive]:
                      searchFlowStore.isWebSearchEnabled,
                  })}
                  before={
                    <Checkbox
                      size="tiny"
                      checked={searchFlowStore.isWebSearchEnabled}
                    />
                  }
                  color={
                    searchFlowStore.isWebSearchEnabled
                      ? "primary"
                      : "secondary-solid"
                  }
                  onClick={() => {
                    searchFlowStore.enableWebSearch(
                      !searchFlowStore.isWebSearchEnabled
                    )
                  }}
                >
                  Web Research
                </Chip>
              </TooltipTrigger>
              <TooltipContent>
                Include targeted web sources alongside document search
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <List
          direction="row"
          gutter="8"
          align="center"
          className={styles.formatGroup}
        >
          {ANSWER_FORMAT_OPTIONS.map((option) => {
            const isActive = searchFlowStore.answerFormat === option.value

            return (
              <Tooltip
                key={option.value}
                color="primary"
                delay={600}
                closeOnTargetPress
              >
                <TooltipTrigger>
                  <Button
                    title={option.label}
                    color="default"
                    variant={isActive ? "contained" : "outlined"}
                    size={isActive ? "medium" : "small"}
                    className={clsx(styles.formatButton, {
                      [styles.formatButtonActive]: isActive,
                    })}
                    disabled={isSearching}
                    onClick={() => searchFlowStore.setAnswerFormat(option.value)}
                  >
                    {option.label}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{option.description}</TooltipContent>
              </Tooltip>
            )
          })}
        </List>
      </div>
    </div>
  )
})

export default SearchWidget
